"use client";

import { AnimatePresence, motion, type Transition } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import useMeasure from "react-use-measure";
import { HeroIcon } from "./hero-icon";

export type PayButtonStatus = "processing" | "success" | "error" | "idle";

type PayButtonProps = {
  amount?: number;
  /** Overrides the idle "Pay {amount}" text. */
  label?: string;
  status?: PayButtonStatus;
  type?: "button" | "submit";
};

const baseTransition: Transition = {
  duration: 0.27,
  ease: [0.25, 1, 0.5, 1],
  type: "spring",
};

export const PayButton = ({
  amount = 0,
  label,
  status = "idle",
  type = "button",
}: PayButtonProps) => {
  const t = useTranslations("common");
  const [elementRef, bounds] = useMeasure();

  useEffect(() => {
    import("ldrs").then(({ ring2 }) => ring2.register());
  }, []);

  const statusText = (status: PayButtonStatus): string => {
    switch (status) {
      case "idle":
        if (label) return label;
        return t("pay", {
          amount: amount.toLocaleString("es-MX", {
            style: "currency",
            currency: "MXN",
          }),
        });
      case "processing":
        return t("processing");
      case "success":
        return t("successful");
      case "error":
        return t("rejected");
    }
  };

  const text = statusText(status);

  const splitWords = () => {
    const words = text.trim().split(" ");
    const midPoint = words.length / 2;
    // TODO: add a word counter and use that for key/layoutId so we reposition the correct word to the correct place
    return words.map((word, index) => (
      <motion.span
        key={`fb-${word}`}
        layoutId={`fb-${word}`}
        layout="position"
        className="inline-block"
        transition={{
          ...baseTransition,
          bounce: 0,
        }}
        initial={{
          opacity: 0,
          filter: "blur(2px)",
          transform:
            index >= midPoint ? "translateX(2rem)" : "translateX(-2rem)",
        }}
        animate={{
          opacity: 1,
          transform: "translateX(0)",
          filter: "blur(0px)",
        }}
        exit={{
          opacity: 0,
          filter: "blur(2px)",
          transform:
            index >= midPoint ? "translateX(2rem)" : "translateX(-2rem)",
        }}
      >
        {word}
      </motion.span>
    ));
  };

  const renderStatusIndicator = () => {
    if (status === "idle") return null;

    if (status === "processing")
      return (
        <l-ring-2
          size="16"
          stroke="3"
          stroke-length="0.25"
          bg-opacity="0.4"
          speed="0.5"
          color="white"
        />
      );

    if (status === "success")
      return (
        <HeroIcon
          name="check-circle"
          className="h-5 w-5 text-white"
          iconStyle="mini"
        />
      );

    if (status === "error")
      return (
        <HeroIcon
          name="x-circle"
          className="h-5 w-5 text-white"
          iconStyle="mini"
        />
      );

    return null;
  };

  return (
    <motion.div
      className="overflow-hidden rounded-full"
      animate={{
        width: bounds.width,
        backgroundColor:
          status === "success"
            ? "#5DD57B"
            : status === "error"
              ? "#FF4D4D"
              : status === "processing"
                ? "#2A2A2A"
                : "#000000",
        scale: status === "success" ? [1.05, 1] : 1,
        x: status === "error" ? [0, -6, 6, -6, 6, 0] : 0,
      }}
      transition={{
        width: {
          ...baseTransition,
          bounce: 0.2,
        },
        backgroundColor: {
          duration: 0.25,
          ease: "easeOut",
        },
        scale: {
          type: "spring",
          stiffness: 500,
          damping: 18,
        },
        x: {
          duration: 0.4,
          ease: "easeInOut",
        },
      }}
    >
      <button
        type={type}
        className="flex h-12 cursor-pointer flex-row items-center space-x-1 px-4 font-medium text-white"
        ref={elementRef}
        disabled={status !== "idle"}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {status !== "idle" && (
            <motion.div
              key={status}
              className="mr-2! flex items-center justify-center"
              initial={{ opacity: 0, transform: "scale(0.9)" }}
              animate={{ opacity: 1, transform: "scale(1)" }}
              exit={{ opacity: 0, transform: "scale(0.9)" }}
              transition={{
                ...baseTransition,
                bounce: 0.1,
              }}
            >
              {renderStatusIndicator()}
            </motion.div>
          )}
          {splitWords()}
        </AnimatePresence>
      </button>
    </motion.div>
  );
};
